import { Breadcrumb } from "@gull";
import { useCallback, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

import HandyScroll from "app/common/Scroll";
import usePagePermission from "app/hooks/usePagePermission";
import queryString from "app/services/queryStringService";
import moment from "moment";
import { Helmet } from "react-helmet";

import { addUpdateArea, deleteArea, getArea } from "app/services/RegionService";

import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";

import { FaUserCheck } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

import { NotificationManager } from "react-notifications";
import Swal from "sweetalert2";
import RegionAutoComplete from "../Components/RegionAutoComplete";

function Area() {
  const { canAdd, canView, canEdit, canDelete } = usePagePermission();

  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);

  const initialData = {
    id: 0,
    areaName: "",
    description: "",
    regionId: 0,
    region: null,
    status: true,
  };

  const [formData, setFormData] = useState(initialData);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  // ===============================
  // Input Change
  // ===============================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialData);
  };

  // ===============================
  // Submit
  // ===============================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setBtnLoading(true);

      const payload = {
        id: formData.id,
        areaName: formData.areaName,
        description: formData.description,
        regionId: formData.regionId,
        status: formData.status,
      };

      const res = await addUpdateArea(payload);

      if (res.data.status === "Success") {
        NotificationManager.success(res.data.message);
      } else {
        NotificationManager.error(res.data.message);
      }

      resetForm();
      loadArea();
    } catch (error) {
      console.error(error);

      NotificationManager.error("Something went wrong");
    } finally {
      setBtnLoading(false);
    }
  };

  // ===============================
  // Edit
  // ===============================

  const handleEdit = async (row) => {
    setFormData({
      id: row.id || 0,
      areaName: row.areaName || "",
      description: row.description || "",
      regionId: row.regionId || 0,
      region: {
        id: row.regionId,
        label: row.regionName,
      },
      status: row.status ?? true,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  // ===============================
  // Delete
  // ===============================

  const handleDelete = async (row) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `You want to delete "${row.areaName}" area`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        const res = await deleteArea(row.id);

        Swal.fire({
          title: "Deleted!",
          text: res?.data?.message,
          icon: res?.data?.status?.toLowerCase(),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
        });

        loadArea();

        if (formData.id === row.id) {
          resetForm();
        }
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text: "Something went wrong",
        icon: "error",
      });
    }
  };

  // ===============================
  // Formatters
  // ===============================

  const dateFormatter = useCallback((cell) => {
    return cell ? (
      <div>
        <div>{moment(cell).format("DD/MM/YYYY")}</div>
        <div>{moment(cell).format("HH:mm")}</div>
      </div>
    ) : (
      "-"
    );
  }, []);

  const statusFormatter = useCallback((cell) => {
    return cell !== undefined && cell !== null ? (
      <span
        style={{
          color: cell ? "green" : "red",
          fontWeight: "600",
        }}
      >
        {cell ? "Active" : "Inactive"}
      </span>
    ) : (
      "-"
    );
  }, []);

  // ===============================
  // Action Dropdown
  // ===============================

  const actionFormatter = (cell, row) => {
    return (
      <>
        <DropdownButton variant="outline-primary" title="Action" size="sm">
          <Dropdown.Item
            className="text-primary"
            onClick={() => handleEdit(row)}
          >
            <FaUserCheck className="me-2" />
            Edit Area
          </Dropdown.Item>

          <OverlayTrigger
            placement="top"
            overlay={
              row.status ? (
                <Tooltip>This area is active and cannot be deleted</Tooltip>
              ) : (
                <></>
              )
            }
          >
            <span>
              <Dropdown.Item
                disabled={row.status}
                className={row.status ? "text-muted" : "text-danger"}
                onClick={() => !row.status && handleDelete(row)}
              >
                <MdDelete className="me-2" />
                Delete Area
              </Dropdown.Item>
            </span>
          </OverlayTrigger>
        </DropdownButton>
      </>
    );
  };

  // ===============================
  // Table Columns
  // ===============================

  const columns = [
    {
      dataField: "id",
      text: "Action",
      formatter: actionFormatter,
      headerStyle: () => ({
        width: "150px",
        textAlign: "center",
      }),
    },
    {
      dataField: "regionName",
      text: "Region Name",
      headerStyle: () => ({
        width: "150px",
        textAlign: "center",
      }),
    },
    {
      dataField: "areaName",
      text: "Area Name",
      headerStyle: () => ({
        width: "150px",
        textAlign: "center",
      }),
    },
    {
      dataField: "description",
      text: "Description",
      headerStyle: () => ({
        width: "200px",
        textAlign: "center",
      }),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: statusFormatter,
      headerStyle: () => ({
        width: "150px",
        textAlign: "center",
      }),
    },
    {
      dataField: "createdAt",
      text: "Created At",
      formatter: dateFormatter,
      headerStyle: () => ({
        width: "180px",
        textAlign: "center",
      }),
    },
    {
      dataField: "updatedAt",
      text: "Updated At",
      formatter: dateFormatter,
      headerStyle: () => ({
        width: "180px",
        textAlign: "center",
      }),
    },
  ];

  // ===============================
  // Table Row Style
  // ===============================

  function rowStyle() {
    return {
      textAlign: "center",
      fontWeight: "600",
      verticalAlign: "middle",
    };
  }

  // ===============================
  // No Data
  // ===============================

  function NoDataIndication() {
    if (loading) {
      return (
        <div className="jumbotron jumbotron-fluid min-vh-80 m-0 bg-white"></div>
      );
    }

    return (
      <div className="jumbotron min-vh-80 text-middle m-0 text-center d-flex flex-column justify-content-center">
        <p>No Data Found</p>
      </div>
    );
  }

  // ===============================
  // Load Area
  // ===============================

  const loadArea = useCallback(async () => {
    try {
      setLoading(true);

      const query = queryString.getQueryStringFromObj({
        pageNumber: page,
        pageSize: sizePerPage,
        ...filters,
      });

      const response = await getArea(query);
      const paginationData = JSON.parse(response.headers["x-pagination"]);
      setDataCount(paginationData.TotalCount);
      setData(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, sizePerPage]);

  useEffect(() => {
    loadArea();
  }, [loadArea]);

  // ===============================
  // Pagination
  // ===============================

  const handleTableChange = useCallback((type, { page, sizePerPage }) => {
    setPage(page);
    setSizePerPage(sizePerPage);
  }, []);

  const sizePerPageRenderer = ({
    options,
    currSizePerPage,
    onSizePerPageChange,
  }) => (
    <>
      <label className="mr-2">Page Size</label>

      <div className="btn-group mr-4" role="group">
        {options.map((option) => {
          const isSelect = currSizePerPage === `${option.page}`;

          return (
            <button
              key={option.text}
              type="button"
              onClick={() => onSizePerPageChange(option.page)}
              className={`btn btn-sm ${
                isSelect ? "btn-primary" : "btn-secondary"
              }`}
            >
              {option.text}
            </button>
          );
        })}
      </div>
    </>
  );

  const paginationOptions = {
    paginationSize: 5,
    pageStartIndex: 1,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    showTotal: true,
    totalSize: dataCount,
    sizePerPageList: [
      {
        text: "10",
        value: 10,
      },
      {
        text: "15",
        value: 15,
      },
      {
        text: "20",
        value: 20,
      },
      {
        text: "50",
        value: 50,
      },
    ],
    sizePerPageRenderer,
  };

  return (
    <>
      <Helmet>
        <title>Area</title>
      </Helmet>

      <Breadcrumb routeSegments={[{ name: "Area" }]} />

      {/* =========================
          Add / Update Form
      ========================== */}

      <Card className="mb-4">
        <Card.Header className="bg-primary text-white fw-bold">
          {formData.id !== 0 ? "Update Area" : "Add Area"}
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* Region AutoComplete */}

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Region</Form.Label>

                  <RegionAutoComplete
                    value={formData.region}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        regionId: selected?.id || 0,
                        region: selected || null,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>

              {/* Area Name */}

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Area Name</Form.Label>

                  <Form.Control
                    type="text"
                    name="areaName"
                    value={formData.areaName}
                    onChange={handleChange}
                    placeholder="Enter Area Name"
                    required
                  />
                </Form.Group>
              </Col>

              {/* Description */}

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>

                  <Form.Control
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter Description"
                  />
                </Form.Group>
              </Col>

              {/* Status */}

              <Col md={1}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>

                  <div>
                    <Form.Check
                      type="switch"
                      id="status-switch"
                      label={formData.status ? "Active" : "Inactive"}
                      name="status"
                      checked={formData.status}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>
              </Col>

              {/* Buttons */}

              <Col
                md={2}
                className="d-flex align-items-center justify-content-center"
              >
                <div className="mt-2">
                  <Button
                    type="submit"
                    variant={formData.id !== 0 ? "warning" : "primary"}
                    disabled={btnLoading}
                  >
                    {btnLoading
                      ? "Please Wait..."
                      : formData.id !== 0
                        ? "Update"
                        : "Add"}
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    className="ms-2"
                    onClick={resetForm}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* =========================
          Table
      ========================== */}

      <HandyScroll>
        <div
          className="spacious-container"
          data-handy-scroll
          style={{ overflow: "auto" }}
        >
          <BootstrapTable
            hover
            remote
            keyField="id"
            data={data}
            columns={columns}
            pagination={paginationFactory(paginationOptions)}
            onTableChange={handleTableChange}
            noDataIndication={() => <NoDataIndication />}
            headerClasses="bg-primary text-white"
            bodyClasses="react-bootstrap-table-next-min-height"
            rowStyle={rowStyle}
          />
        </div>
      </HandyScroll>
    </>
  );
}

export default Area;
